<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import AppLoader from '@/components/AppLoader.vue'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { showConfirm, errorLoading, webAlert } from '@/utils/dialog'
import { useRouter } from 'vue-router'

const router = useRouter()

// --- Ref do elementu textarea ---
const textareaRef = ref(null)

// --- 1. Konfiguracja Easy Data Table ---
const headers = [
    { text: 'ID', value: 'ID', sortable: true, width: 70 },
    { text: 'Nazwa szablonu', value: 'name', sortable: true, width: 250 },
    { text: 'Szablon treści', value: 'template' },
    { text: '', value: 'actions', width: 100 }
]

const items = ref([])
const loading = ref(false)
const saving = ref(false)
const savingTXT = ref('')
const searchValue = ref('')

const serverItemsLength = ref(0)
const serverOptions = ref({
    page: 1,
    rowsPerPage: 10,
    sortBy: 'ID',
    sortType: 'desc'
})

// --- 2. Pobieranie danych z API ---
const loadData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                method: 'post',
                action: 'get-description-generators',
                page: serverOptions.value.page,
                limit: serverOptions.value.rowsPerPage,
                sortBy: serverOptions.value.sortBy,
                sortType: serverOptions.value.sortType,
                search: searchValue.value
            }
        )

        items.value = Array.isArray(response.data?.data) ? response.data.data : []
        serverItemsLength.value = response.data?.total || 0
    } catch (error) {
        errorLoading()
    } finally {
        loading.value = false
    }
}

watch(
    serverOptions,
    () => {
        loadData()
    },
    { deep: true }
)

let searchTimeout = null
const handleSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        serverOptions.value.page = 1
        loadData()
    }, 400)
}

// --- 3. Obsługa Modala i Formularza ---
const modalRef = ref(null)
const formRef = ref(null)
let modalInstance = null

const formTitle = ref('Dodaj szablon')
const formData = ref({
    ID: '',
    name: '',
    template: ''
})

const openAddModal = () => {
    formTitle.value = 'Dodaj nowy szablon opisu'
    formData.value = { ID: '', name: '', template: '' }
    modalInstance.show()
}

const openEditModal = async (id) => {
    savingTXT.value = 'Pobieranie danych ...'
    saving.value = true
    try {
        const response = await apiClient.post({
                action: 'get-description-generators',
                ID: id
        })

        const result = response.data
        const item = result.data[0]

        formTitle.value = `Edytuj: ${item.name}`
        formData.value = { ...item }
        modalInstance.show()
    } catch (error) {
        errorLoading()
    } finally {
        saving.value = false
    }
}

const handleSubmit = async () => {
    savingTXT.value = 'Zapisywanie ...'
    saving.value = true
    try {
        const response = await apiClient.post(
            {
                action: 'save-description-generator',
                id: formData.value.ID || null,
                name: formData.value.name,
                template: formData.value.template
            }
        )

        if (!response.data?.role) {
            localStorage.removeItem('integrator_token')
            localStorage.removeItem('user_email')
            localStorage.removeItem('user_role')
            localStorage.removeItem('admin_email')
            router.push('/admin/login')
        } else {
            localStorage.setItem('user_role', response.data.role)
        }

        modalInstance.hide()
        saving.value = false
        await loadData()

        webAlert(response.data)

    } catch (error) {
        errorLoading()
    }
}

const deleteItem = async (id) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć ten szablon opisu?', {
        title: 'Usuwanie pozycji',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })

    if (!confirmed) return

    saving.value = true
    savingTXT.value = "Kasowanie ..."
    try {
        const response = await apiClient.post(
            {
                action: 'delete-description-generator',
                id: id
            }
        )

        saving.value = false
        await loadData()
        webAlert(response.data)
    } catch (error) {
        errorLoading()
    } finally {
        saving.value = false
    }
}

// Inteligentne wstawianie/podmienianie zmiennej w miejscu kursora lub zaznaczenia
const insertPlaceholder = (tag) => {
    const textToInsert = `{${tag}}`
    const textarea = textareaRef.value

    if (!textarea) {
        formData.value.template += textToInsert
        return
    }

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentValue = formData.value.template || ''

    // Zapamiętanie obecnej pozycji przewijania textarea
    const scrollTop = textarea.scrollTop

    // Podmiana zaznaczonego tekstu lub wstawienie w miejscu kursora
    formData.value.template = currentValue.substring(0, start) + textToInsert + currentValue.substring(end)

    nextTick(() => {
        // Przywrócenie pierwotnej pozycji przewijania
        textarea.scrollTop = scrollTop

        // Ustawienie focusa bez automatycznego skrollowania
        textarea.focus({ preventScroll: true })

        // Ustawienie kursora bezpośrednio za wstawionym tagiem
        const newCursorPos = start + textToInsert.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
    })
}

onMounted(() => {
    modalInstance = new Modal(modalRef.value)

    modalRef.value.addEventListener('shown.bs.modal', () => {
        const firstInput = modalRef.value.querySelector('input:not([type="hidden"]), select, textarea')
        if (firstInput) {
            firstInput.focus()
        }
    })

    loadData()
})
</script>

<template>
    <div class="container-fluid">

        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Generator treści ogłoszenia</h3>
                <p class="text-muted small mb-0">Zarządzanie szablonami i regułami automatycznej generacji treści</p>
            </div>
            <button
                class="btn btn-primary-dark d-flex align-items-center justify-content-center gap-2 w-100 btn-responsive-auto"
                @click="openAddModal">
                <i class="bi bi-plus-lg"></i>
                <span>Dodaj nowy szablon</span>
            </button>
        </div>

        <div class="card border-0 shadow-sm">
            <div class="card-body">
                <div class="row">
                    <div class="col-12 col-md-8"></div>
                    <div class="col-12 col-md-4">
                        <input v-model="searchValue" @input="handleSearch" type="text" class="form-control mb-3"
                            placeholder="Szukaj..." />
                    </div>
                </div>

                <div class="table-responsive">
                    <Vue3EasyDataTable v-model:server-options="serverOptions" :headers="headers" :items="items"
                        :loading="loading" :server-items-length="serverItemsLength" buttons-pagination
                        class="table-font-13" table-class-name="table table-hover align-middle custom-easy-table"
                        :rows-items="[10, 25, 50, 100]" empty-message="Brak danych do wyświetlenia"
                        rows-per-page-message="Wierszy na stronę:">

                        <template #loading>
                            <div class="py-4 text-center">
                                Pobieranie danych, proszę czekać...
                            </div>
                        </template>

                        <template #item-template="item">
                            <span class="text-truncate d-inline-block" style="max-width: 450px;">
                                {{ item.template }}
                            </span>
                        </template>

                        <template #item-actions="item">
                            <div class="d-flex justify-content-end gap-1">
                                <button class="btn btn-sm btn-primary-light" title="Edytuj"
                                    @click="openEditModal(item.ID)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" title="Usuń" @click="deleteItem(item.ID)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- Modal Formularza -->
        <div ref="modalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">{{ formTitle }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <form ref="formRef" @submit.prevent="handleSubmit">
                        <div class="modal-body">
                            <input type="hidden" name="id" :value="formData.ID" />

                            <div class="mb-3">
                                <label class="form-label fw-bold">Nazwa generatora / szablonu</label>
                                <input type="text" class="form-control" v-model="formData.name"
                                    placeholder="np. Standardowy opis pojazdu" required />
                            </div>

                            <div class="mb-2">
                                <label class="form-label fw-bold">Treść szablonu</label>
                                <textarea ref="textareaRef" class="form-control" rows="8" v-model="formData.template"
                                    placeholder="Wpisz treść szablonu. Możesz używać znaczników w nawiasach klamrowych..."
                                    required></textarea>
                            </div>

                            <!-- Pomocnicza lista tagów -->
                            <div class="bg-light p-2 rounded border mb-3">
                                <small class="text-muted d-block mb-1 fw-bold">Szybkie wstawianie zmiennych:</small>
                                <div class="d-flex flex-wrap gap-1">
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Marka')">{Marka}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Model')">{Model}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Generacja')">{Generacja}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Rok')">{Rok}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Przebieg')">{Przebieg}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Pojemnosc')">{Pojemnosc}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Moc')">{Moc}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Paliwo')">{Paliwo}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Skrzynia')">{Skrzynia}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Cena')">{Cena}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('VIN')">{VIN}</button>

                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('PierwszaRejestracja')">{PierwszaRejestracja}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Rejestracja')">{Rejestracja}</button>
                                    <button type="button" class="btn btn-xs btn-outline-secondary "
                                        @click="insertPlaceholder('Wyposazenie')">{Wyposazenie}</button>

                                    </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary">Zapisz szablon</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    <div id="glownyloader">
        <AppLoader :show="saving" :text="savingTXT" />
    </div>
</template>

<style scoped>
.btn-xs {
    font-size: 0.75rem;
}

.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    max-width: calc(100vw - 2rem) !important;
    -webkit-overflow-scrolling: touch;
}

:deep(.vue3-easy-data-table) {
    --easy-table-border: 1px solid #dee2e6;
    --easy-table-row-border: 1px solid #dee2e6;
    --easy-table-header-font-size: 0.85rem;
    --easy-table-header-height: 45px;
    --easy-table-body-row-height: 50px;
    width: 100% !important;
    max-width: 100% !important;
}

:deep(.vue3-easy-data-table__main) {
    width: 100% !important;
    overflow-x: auto !important;
}

.buttons-pagination .item.button.active {
    background-color: #043873 !important;
}
</style>